<script setup>
import { WdsSearchFormInline, WdsSearchFormStacked, WdsSearchFormStandalone, WdsSearchFormWizard } from '@wds/components';
import searchConfig from '@/config/search';
import { replayActiveSearchFocus } from '@/utils/replayActiveSearchFocus.js';

const props = defineProps({
  hideOnSmallScreen: {
    type: Boolean,
    required: false,
    default: false,
  },
  useStandaloneMobile: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// ### STORE/PINIA
const searchStore = useSearchStore();
const locationStore = useLocationStore();
const testingStore = useTestingStore();
const routeStore = useRouteStore();

// ### COMPOSABLES
const nuxtApp = useNuxtApp();
const route = useRoute();
const dateTime = useDateTime();
const searchData = useSearch();
const { validateSearchQuery } = useSearchParams();

const shouldPopulateSearchFormFromQuery = computed(() => routeStore.isSearchResultsPage && validateSearchQuery(route.query || {}));

// ### DATA
const inlineSearchForm = ref(null);
const phoneSearchForm = ref(null);
const searchFormRecentSearches = ref(null);

// ### FEATURE TOGGLES
const newAutoCompleteServiceEnabled = testingStore.hasEnabledFeature('web_newAutoCompleteService');

// ### COMPUTED
const searchFormInlineParams = computed(() => ({
  size: 'large',
  destinationMaxVisibleSearchResults: newAutoCompleteServiceEnabled ? 5 : 3,
  ...searchData.getSearchFormCommonParams.value,
  destinationRecentSearches: searchFormRecentSearches.value,
}));

const searchFormWizardParams = computed(() => ({
  activeStep: searchData.wizardActiveStep.value,
  openWizard: searchData.wizardIsOpen.value,
  focusDestinationOnOpen: true,
  destinationMaxVisibleSearchResults: 0,
  ...searchData.getSearchFormCommonParams.value,
  destinationRecentSearches: searchFormRecentSearches.value,
}));

const searchFormStackedParams = computed(() => ({
  size: 'small',
  ...searchData.getSearchFormCommonParams.value,
  destinationRecentSearches: searchFormRecentSearches.value,
}));

const searchFormStandaloneParams = computed(() => ({ translations: searchData.getSearchFormTranslations.value }));

const getDestinationObject = computed(() => {
  const locationType = locationStore.getLocationType;
  const locationInfo = locationStore.getLocationFullInfo;

  switch (locationType) {
    case 'region':
      // @TODO: COD-67 - Remove after A/B test - geoSpatialBaliEnabled
      // @TODO: COD-461 - Remove after A/B test - geoSpatialSearchEnabled
      if (searchData.isGeoSpatialValidRegion(locationInfo?.region?.id)) {
        return {
          id: locationInfo?.region?.id,
          type: 'region',
          label: locationInfo.region?.name,
          labelSmall: locationInfo.country?.name,
          value: locationInfo.region?.id.toString(),
        };
      }
      return {};
    case 'city':
      if (!locationInfo.city?.id) {
        return {};
      }

      return {
        id: locationInfo.city?.id,
        type: 'city',
        label: locationInfo.city?.name,
        labelSmall: locationInfo.country?.name,
        value: locationInfo.city?.id.toString(),
      };
    case 'district':
      if (!locationInfo.district?.id) {
        return {};
      }

      return {
        id: locationInfo.district?.id,
        cityId: locationInfo.city?.id,
        type: 'citydistricts',
        label: locationInfo.district?.name,
        labelSmall: [locationInfo.city?.name, locationInfo.country?.name].join(', '),
        value: locationInfo.district?.id.toString(),
      };
    case 'property':
      if (!locationInfo.property?.id) {
        return {};
      }

      return {
        id: locationInfo?.property?.id,
        type: 'property',
        label: locationInfo.property?.name,
        labelSlug: locationInfo.property?.slug,
        city: locationInfo?.city?.name,
        value: locationInfo.property?.id.toString(),
      };
    default:
      return {};
  }
});

// ### CREATED
// On /search with valid query: store is hydrated by pages/search/index.vue; skip default location/dates.
if (!shouldPopulateSearchFormFromQuery.value) {
  searchStore.setSearchParam('location', getDestinationObject.value);
  searchStore.setSearchCheckIn(dateTime.tomorrow);
  searchStore.setSearchCheckOut(dateTime.addDaysToDate(dateTime.tomorrow, searchConfig.defaultNights));
}

onMounted(() => {
  replayActiveSearchFocus({
    activeElement: document.activeElement,
    inlineRoot: inlineSearchForm.value?.$el,
    phoneRoot: phoneSearchForm.value?.$el,
    onPhone: () => {
      if (props.useStandaloneMobile) {
        searchData.handleWizardOpen();
        return;
      }

      searchData.handleDestinationFieldClicked();
    },
  });

  // Restore previously inputted values if the page was reloaded (e.g. by the TrustArc consent banner).
  // Skip when the form is driven by URL query params so group type and other values are not overwritten.
  if (!shouldPopulateSearchFormFromQuery.value) {
    const restored = searchStore.restoreSearchParamsFromStorage();
    if (restored) {
      return;
    }
  }

  // Override default check-in and check-out dates on client side because the user might be in a different timezone
  // than the server. Skip when on /search with valid query (store hydrated on the search page).
  if (!shouldPopulateSearchFormFromQuery.value) {
    searchStore.setSearchCheckIn(dateTime.tomorrow);
    searchStore.setSearchCheckOut(dateTime.addDaysToDate(dateTime.tomorrow, searchConfig.defaultNights));
  }

  // Get recent searches for the form on client side because the user might be in a different timezone
  // than the server and avoid hydration mismatch.
  searchFormRecentSearches.value = searchData.getUserRecentSearchesForForm?.value ?? null;
});

// ### EVENT HOOKS
// The search form needs to listen for events from other components to avoid creating duplicate search form components on the application.
nuxtApp.hooks.hook('search-form:open', (tab) => {
  switch (tab) {
    case 'dates':
      searchData.handleDatesFieldClicked();
      break;
    case 'guests':
      searchData.handleGuestsFieldClicked();
      break;
    default:
      searchData.handleDestinationFieldClicked();
  }
});
</script>

<template>
  <div class="search-form-wrapper">
    <WdsSearchFormStacked
      v-if="!props.useStandaloneMobile"
      ref="phoneSearchForm"
      class="search-form-stacked-container"
      :class="{ 'small-screen': hideOnSmallScreen }"
      v-bind="searchFormStackedParams"
      @destination-selected="searchData.handleDestinationFieldClicked"
      @destination-input="searchData.handleDestinationSearch"
      @destination-item-selected="searchData.handleDestinationItemSelected"
      @recent-destination-selected="searchData?.handleRecentDestinationItemSelected"
      @current-location="searchData.handleCurrentLocation"
      @dates-selected="searchData.handleDatesFieldClicked"
      @guests-selected="searchData.handleGuestsFieldClicked"
      @guests-change="searchData.handleGuestsNumberChanged"
      @guests-group-type-change="searchData.handleGuestsGroupTypeChanged"
      @guests-group-ages-change="searchData.handleGuestsGroupAgesChanged"
      @search-form-submit="searchData.handleSubmit"
    />
    <WdsSearchFormStandalone
      v-else
      ref="phoneSearchForm"
      class="search-form-standalone-container"
      v-bind="searchFormStandaloneParams"
      @input-selected="searchData.handleWizardOpen"
    />
    <WdsSearchFormInline
      ref="inlineSearchForm"
      class="search-form-inline-container"
      v-bind="searchFormInlineParams"
      @destination-selected="searchData.handleDestinationFieldClicked"
      @destination-input="searchData.handleDestinationSearch"
      @destination-item-selected="searchData.handleDestinationItemSelected"
      @destination-more-items-selected="searchData?.handleDestinationMoreItemsSelected"
      @recent-destination-selected="searchData?.handleRecentDestinationItemSelected"
      @current-location="searchData.handleCurrentLocation"
      @dates-selected="searchData.handleDatesFieldClicked"
      @dates-changed="searchData.handleDatesSelected"
      @guests-selected="searchData.handleGuestsFieldClicked"
      @guests-change="searchData.handleGuestsNumberChanged"
      @guests-group-type-change="searchData.handleGuestsGroupTypeChanged"
      @guests-group-ages-change="searchData.handleGuestsGroupAgesChanged"
      @search-form-submit="searchData.handleSubmit"
    />
    <ClientOnly>
      <WdsSearchFormWizard
        class="search-form-wizard-container"
        v-bind="searchFormWizardParams"
        @destination-selected="searchData.handleDestinationItemSelected"
        @destination-input="searchData.handleDestinationSearch"
        @destination-item-selected="searchData.handleDestinationItemSelected"
        @destination-more-items-selected="searchData?.handleDestinationMoreItemsSelected"
        @recent-destination-selected="searchData?.handleRecentDestinationItemSelected"
        @current-location="searchData.handleCurrentLocation"
        @guests-change="searchData.handleGuestsNumberChanged"
        @guests-group-type-change="searchData.handleGuestsGroupTypeChanged"
        @guests-group-ages-change="searchData.handleGuestsGroupAgesChanged"
        @search-form-submit="searchData.handleSubmit"
        @search-form-close="searchData.handleWizardClose()"
      />
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
.search-form-wrapper {
  width: 100%;

  .search-form-inline-container {
    display: none;
    margin: 0 auto;
  }

  .small-screen {
    display: none;
  }

  .search-form-standalone-container {
    display: flex;
    flex: 1;
    min-height: wds-rem(64px);
  }

  @include tablet-large {
    .search-form-stacked-container,
    .search-form-wizard-container,
    .search-form-standalone-container {
      display: none;
    }

    .search-form-inline-container {
      display: block;
      max-width: wds-rem(1024px);
    }
  }
}
</style>
